import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { faMoneyCheckDollar } from '@fortawesome/pro-duotone-svg-icons/faMoneyCheckDollar';
import { faChild } from '@fortawesome/pro-solid-svg-icons/faChild';
import { faCat } from '@fortawesome/pro-solid-svg-icons/faCat';
import { faDog } from '@fortawesome/pro-solid-svg-icons/faDog';
import { faPaw } from '@fortawesome/pro-solid-svg-icons/faPaw';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { CancelBookingModal } from 'us-react-components/dist/CancelBookingModal/CancelBookingModal';
import { DeclineBookingModal } from 'us-react-components/dist/DeclineBookingModal/DeclineBookingModal';
import { AccordionItem } from 'us-react-components/dist/AccordionItem/AccordionItem';
import { bookingTypeConstant } from 'us-react-components/dist/constants/Booking';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import { WidgetCard } from 'us-react-components/dist/WidgetCard/WidgetCard';
import {
  Booking,
  BookingService,
  PayoutMethodService,
  PayoutMethodStatus,
  SitterService,
} from 'us-web-services';
import {
  DateFormat,
  formatDateRange,
  formatDateToString,
  getDurationString,
  getShortWeekdayDateString,
  getTimeOfDayString,
} from 'us-react-components/dist/utils/date';
import { fullName } from 'us-react-components/dist/utils/string';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const toApiDateTime = (d: Date): string =>
  formatDateToString(d, DateFormat.API_DATE_TIME_FORMAT);

const sentenceCase = (s?: string): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';

const dateRangeWithDuration = (start?: string, end?: string): string => {
  const range = formatDateRange(start, end, 'long');
  const duration = getDurationString(start, end);
  return duration ? `${range} (${duration})` : range;
};

interface ScheduleEntry {
  id?: number;
  start?: string;
  end?: string;
  booking?: { id?: number; status?: { type?: string } };
}

interface ContactParent {
  id?: number;
  name?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  pictureUrl?: string;
  active?: boolean;
  connections?: unknown[];
}

const getMapLink = (addr?: {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}): string => {
  if (!addr) return '';
  const q = encodeURIComponent(
    [addr.street, addr.city, addr.state, addr.zip].filter(Boolean).join(', '),
  );
  return `https://maps.google.com/?q=${q}`;
};

const BookingDetailsCard = ({
  booking,
}: {
  booking: Booking;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const code = bookingTypeConstant.find((c) => c.id === (booking.typeId ?? 0))?.code
    ?? 'childCare';
  const isChildCare = code === 'childCare';
  const isPetCare = code === 'petCare';
  const isSeniorCare = code === 'seniorCare';
  const isHousehold = code === 'householdServices';
  const numKids = booking.numberOfChildren ?? 0;
  const status = booking.status?.code ?? '';
  const isInterview = booking.type === 'interview';
  const kidWord = (): string => {
    if (isChildCare) return numKids === 1 ? 'child' : 'children';
    if (isPetCare) return numKids === 1 ? 'pet' : 'pets';
    if (isSeniorCare) return numKids === 1 ? 'senior' : 'seniors';
    return '';
  };
  const paymentLabel = booking.paymentType === 'credit'
    ? 'Credit card'
    : booking.paymentType === 'cash'
      ? 'Cash or check'
      : '';

  return (
    <div id='booking-details' className='us-card rounded-[20px] overflow-hidden'>
      <AccordionItem
        title={t('pages.bookingDetail.detailsHeading', { type: sentenceCase(booking.type) || t('pages.bookingDetail.bookingType') })}
        defaultOpen
        headerClassName='bg-gray-100'
      >
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <div>
          {isInterview && booking.interviewType && (
            <div className='mb-2'>
              <b>Type:</b> {booking.interviewType} interview
            </div>
          )}

          <div className='mb-2'>
            <b>Care for:</b>{' '}
            {isHousehold ? (
              <>Household services only</>
            ) : (
              <>
                {numKids} {kidWord()}
              </>
            )}
            {isChildCare &&
              Array.from({ length: numKids }).map((_, i) => (
                <FontAwesomeIcon
                  key={i}
                  icon={faChild}
                  className={`text-sapphire align-middle${i === 0 ? ' ml-2' : ''}`}
                />
              ))}
            {isPetCare && (
              <span className='ml-2'>
                {booking.hasCat && <FontAwesomeIcon icon={faCat} className='mr-1' />}
                {booking.hasDog && <FontAwesomeIcon icon={faDog} className='mr-1' />}
                {booking.hasOtherPet && <FontAwesomeIcon icon={faPaw} className='mr-1' />}
              </span>
            )}
          </div>

          {booking.kidsAges && !isPetCare && !isHousehold && (
            <div className='mb-2'>
              <b>Age{numKids === 1 ? '' : 's'}:</b> {booking.kidsAges}
            </div>
          )}

          {(!isInterview || booking.interviewType !== 'phone') && (
            <div className='mb-2'>
              <b>Rate:</b> ${booking.rate}
              {booking.paymentFrequency?.id === 2
                ? ` flat for the ${booking.type}`
                : '/hr'}
            </div>
          )}

          {booking.rate != null && (
            <div className='mb-2'>
              <b>Payment type:</b> {paymentLabel}
            </div>
          )}

          {booking.address && (booking.type === 'job' || booking.interviewType !== 'phone') && (
            <>
              {booking.address.neighborhood && (
                <div className='mb-2'>
                  <b>Neighborhood:</b> {booking.address.neighborhood}
                </div>
              )}
              <div className='mb-2'>
                <b>Location:</b>
                <a
                  id='booking-address-link'
                  target='_blank'
                  rel='noreferrer'
                  href={getMapLink(booking.address)}
                  className='block underline'
                >
                  {booking.address.street && (
                    <>
                      {booking.address.street}
                      <br />
                    </>
                  )}
                  {booking.address.street2 && (
                    <>
                      {booking.address.street2}
                      <br />
                    </>
                  )}
                  {booking.address.city}, {booking.address.state} {booking.address.zip}
                </a>
              </div>
            </>
          )}

          {!status.includes('reject') && (
            <div className='mb-2'>
              <b>Family&apos;s phone:</b>{' '}
              {status === 'pending' ? (
                <i>shown only if accepted</i>
              ) : (
                <span>{booking.parent?.phone ?? <i>not provided</i>}</span>
              )}
            </div>
          )}
        </div>
        <div>
          {isChildCare && booking.includes && booking.includes.length > 0 && (
            <div className='mb-3'>
              <h4 className='font-semibold mb-1'>Job includes:</h4>
              <ul className='list-disc pl-5'>
                {booking.includes.map((inc) => (
                  <li key={inc.type} id={`include-${inc.type}`}>
                    {inc.label}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {isChildCare && booking.sweeteners && booking.sweeteners.length > 0 && (
            <div className='mb-3'>
              <h4 className='font-semibold mb-1'>Sweeten the deal:</h4>
              <ul className='list-disc pl-5'>
                {booking.sweeteners.map((s) => (
                  <li key={s.type} id={`sweetener-${s.type}`}>
                    {s.label}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {booking.description && (
          <div className='col-span-full'>
            <h4 className='font-semibold mb-1'>Job description:</h4>
            <div className='bg-sapphire-50 p-3 rounded'>{booking.description}</div>
          </div>
        )}
      </div>
      </AccordionItem>
    </div>
  );
};

const PAYOUT_WIDGET_STATUSES = [
  'accepted',
  'charge_failed_hard',
  'closed_completed',
  'deposit_failed_hard',
  'job_finished',
  'processing_charge',
  'processing_deposit',
  'sitter_confirmed',
];

export const BookingDetail = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores, services: { tracker } } = useDependencies();
  const user = stores.user.user;

  type LoadState = 'loading' | 'loaded' | 'not-found' | 'forbidden' | 'error';
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [availabilities, setAvailabilities] = useState<ScheduleEntry[]>([]);
  const [showPayoutWidget, setShowPayoutWidget] = useState(false);
  const [payoutModalOpen, setPayoutModalOpen] = useState(false);

  const trackBookingAction = (action: string): void => {
    void tracker.track('booking-details', {
      action,
      bookingId: booking?.id,
      status: booking?.status?.code,
    });
  };

  const hasActivePayoutMethod = async (): Promise<boolean> => {
    if (!user?.id) return true;
    try {
      const resp = await PayoutMethodService.findByFilter({
        params: { sitterId: user.id, userId: user.id },
      });
      const methods = (resp.data?.data ?? []) as { status?: PayoutMethodStatus }[];
      return methods.some((m) => m.status === PayoutMethodStatus.ACTIVE);
    } catch {
      return true;
    }
  };

  const acceptBooking = async (): Promise<void> => {
    if (!booking?.id || accepting) return;
    setAccepting(true);
    trackBookingAction('accept-booking');
    if (booking.paymentType === 'credit') {
      const ok = await hasActivePayoutMethod();
      if (!ok) {
        setAccepting(false);
        setPayoutModalOpen(true);
        return;
      }
    }
    try {
      const resp = await BookingService.patch(booking.id, {
        id: booking.id,
        status: { code: 'accepted' },
      } as never);
      const updated = (resp.data?.data ?? null) as Booking | null;
      if (updated) setBooking(updated);
      toast.success(t('pages.bookingDetail.acceptSuccess'));
    } catch (err: unknown) {
      const code = (err as { response?: { data?: { errors?: Array<{ code?: string }> } } })
        ?.response?.data?.errors?.[0]?.code;
      if (code === 'booking.payout_setup') {
        setPayoutModalOpen(true);
      } else {
        toast.error(t('pages.bookingDetail.acceptError'));
      }
    } finally {
      setAccepting(false);
    }
  };

  const checkPayoutWidget = async (b: Booking): Promise<void> => {
    if (b.paymentType !== 'credit') return;
    const statusCode = b.status?.code ?? '';
    if (!PAYOUT_WIDGET_STATUSES.includes(statusCode)) return;
    if (!user?.id) return;
    try {
      const resp = await PayoutMethodService.findByFilter({
        params: { sitterId: user.id, userId: user.id },
      });
      const methods = (resp.data?.data ?? []) as { status?: PayoutMethodStatus }[];
      const hasActive = methods.some((m) => m.status === PayoutMethodStatus.ACTIVE);
      if (!hasActive) setShowPayoutWidget(true);
    } catch {
      // non-critical
    }
  };

  useEffect(() => {
    document.title = 'UrbanSitter - Booking';
  }, []);

  useEffect(() => {
    const bookingId = Number(pathId);
    if (!bookingId || !user?.id) return;
    const controller = new AbortController();
    setLoadState('loading');
    const load = async (): Promise<void> => {
      try {
        const resp = await BookingService.get(bookingId, { params: { userId: user.id }, signal: controller.signal });
        const b = (resp.data?.data ?? null) as Booking | null;
        setBooking(b);
        setLoadState('loaded');
        if (b) void checkPayoutWidget(b);
      } catch (err) {
        const e = err as { code?: string; response?: { status?: number } };
        if (e?.code === 'ERR_CANCELED') return;
        const status = e?.response?.status;
        if (status === 404) setLoadState('not-found');
        else if (status === 403) setLoadState('forbidden');
        else setLoadState('error');
      }
    };
    void load();
    return (): void => controller.abort();
  }, [pathId, user?.id, loadAttempt]);

  // Load sitter's availability for the booking day when status is pending.
  useEffect(() => {
    if (!booking?.start || booking?.status?.code !== 'pending') return;
    const sitterId = booking.sitter?.id;
    if (!sitterId) return;
    const day = new Date(booking.start);
    if (Number.isNaN(day.getTime())) return;
    const dayStart = new Date(day); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(day); dayEnd.setHours(23, 59, 59, 0);
    const load = async (): Promise<void> => {
      try {
        const resp = await SitterService.getSchedules({
          params: {
            sitterId,
            start: toApiDateTime(dayStart),
            end: toApiDateTime(dayEnd),
          },
        });
        setAvailabilities((resp.data?.data ?? []) as ScheduleEntry[]);
      } catch {
        setAvailabilities([]);
      }
    };
    void load();
  }, [booking?.id, booking?.status?.code]);

  if (loadState === 'loading') {
    return (
      <div className='container py-8'>
        <i className='fa-solid fa-spinner fa-spin mr-2' />
        <span>{t('pages.bookingDetail.loading')}</span>
      </div>
    );
  }

  if (loadState === 'not-found' || loadState === 'forbidden' || loadState === 'error') {
    return (
      <div className='container py-8'>
        <Button
          buttonType={ButtonType.tertiary}
          onClick={(): void => navigate('/bookings')}
          className='text-sm underline cursor-pointer bg-transparent border-0 p-0 mb-4'
        >
          {t('pages.bookingDetail.backToList')}
        </Button>
        <div className='us-card rounded-[20px]'>
          <h2 className='text-xl font-semibold mb-2'>
            {t(loadState === 'error' ? 'common.errors.loadFailedTitle' : `common.errors.${loadState === 'not-found' ? 'notFound' : 'forbidden'}`)}
          </h2>
          <p className='mb-4'>
            {t(loadState === 'error' ? 'common.errors.loadFailedBody' : `common.errors.${loadState === 'not-found' ? 'notFoundBody' : 'forbiddenBody'}`)}
          </p>
          {loadState === 'error' && (
            <Button
              buttonType={ButtonType.primary}
              onClick={(): void => setLoadAttempt((n) => n + 1)}
            >
              {t('common.errors.retry')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className='container py-8'>
        <i className='fa-solid fa-spinner fa-spin mr-2' />
        <span>{t('pages.bookingDetail.loading')}</span>
      </div>
    );
  }

  const contact = booking.parent as ContactParent | undefined;
  const status = booking.status?.code ?? '';
  const isPending = status === 'pending';
  const isParentReschedule = status === 'parent_reschedule';
  const isRescheduled = status.includes('reschedule');
  const isSitterConfirmed = status === 'sitter_confirmed';
  const isPhoneInterview =
    booking.type === 'interview' && booking.interviewType === 'phone';

  const showAvailabilitySidebar = isPending && !!contact?.active && availabilities.length > 0;
  const showRightSidebar = showAvailabilitySidebar || showPayoutWidget;

  return (
    <div className='container py-8 max-w-5xl'>
      <Button
        buttonType={ButtonType.tertiary}
        onClick={(): void => navigate('/bookings')}
        className='text-sm underline cursor-pointer bg-transparent border-0 p-0'
      >
        {t('pages.bookingDetail.backToList')}
      </Button>
      <h1 className='text-2xl font-semibold mt-3 mb-2'>
        {booking.title ?? formatDateRange(booking.start, booking.end, 'long')}
      </h1>
      <div className='mb-4'>{booking.subTitle ?? booking.status?.label}</div>

      <div className={`grid gap-6 items-start ${showRightSidebar ? 'lg:grid-cols-3' : ''}`}>
        <div className={showRightSidebar ? 'lg:col-span-2' : ''}>
          {/* Parent info / summary card */}
          <div className='us-card rounded-[20px]'>
            <div className='flex items-start gap-4 mb-4'>
              {contact?.pictureUrl && (
                <div className='relative w-20 h-20 flex-shrink-0'>
                  <span className={`absolute -left-[9px] -top-[8px] w-[22px] h-[22px] bg-contain bg-no-repeat icon-svg-${booking.status?.webIcon ?? ''}`} />
                  <img
                    src={contact.pictureUrl}
                    alt={fullName(contact)}
                    className='w-20 h-20 rounded-full object-cover'
                  />
                </div>
              )}
              <div className='flex-1'>
                <h3 className='font-semibold text-lg'>
                  <a href={`/app/parent/${contact?.id}`} className='underline'>
                    {sentenceCase(contact?.name)}
                  </a>
                  {!contact?.active && (
                    <span className='text-neutral-500 font-normal text-sm ml-2'>
                      is no longer active
                    </span>
                  )}
                </h3>
                <div>{dateRangeWithDuration(booking.start, booking.end)}</div>
                {isPhoneInterview && contact?.firstName && (
                  <div className='text-sm text-neutral-600 italic mt-1'>
                    -- {contact.firstName} will call you
                  </div>
                )}
              </div>
            </div>

            {(isRescheduled || isSitterConfirmed) && (
              <div className='grid grid-cols-2 gap-4 text-center mt-2 mb-2'>
                <div className='border-r border-neutral-200 pr-4'>
                  <h5 className='font-semibold text-xs uppercase mb-1'>
                    {t(isRescheduled ? 'pages.bookingDetail.previousSchedule' : 'pages.bookingDetail.bookedHours')}
                  </h5>
                  <div>{dateRangeWithDuration(booking.originalStart, booking.originalEnd)}</div>
                </div>
                <div className='pl-4'>
                  <h5 className='font-semibold text-xs uppercase mb-1'>
                    {t(isRescheduled ? 'pages.bookingDetail.newSchedule' : 'pages.bookingDetail.confirmedHours')}
                  </h5>
                  <div>{dateRangeWithDuration(booking.start, booking.end)}</div>
                </div>
              </div>
            )}

            {/* CTAs in summary card — only for non-pending actions */}
            {contact?.active && (
              <div className='flex flex-wrap gap-2'>
                {status === 'job_finished' && booking.paymentType === 'credit' && (
                  <Button
                    buttonType={ButtonType.primary}
                    onClick={(): void => {
                      trackBookingAction('confirm-hours');
                      navigate(`/bookings/${booking.id}/confirm-hours`);
                    }}
                  >
                    {t('pages.bookingDetail.confirmHours')}
                  </Button>
                )}
                {status === 'deposit_failed_hard' && (
                  <Button
                    buttonType={ButtonType.primary}
                    onClick={(): void => navigate('/sitter/payout')}
                  >
                    Update Your Payout Information
                  </Button>
                )}
                {!isPending && (
                  <Button
                    buttonType={ButtonType.secondary}
                    onClick={(): void => {
                      navigate(`/sitter/${booking.sitter?.id}/messages/${contact?.id}`);
                    }}
                  >
                    Message
                  </Button>
                )}
                {(status === 'accepted' || status === 'sitter_reschedule') && (
                  <Button
                    buttonType={ButtonType.secondary}
                    onClick={(): void => {
                      trackBookingAction('cancel-booking');
                      setCancelOpen(true);
                    }}
                  >
                    Cancel {booking.type}
                  </Button>
                )}
                {booking.type === 'interview' && status === 'accepted' && (
                  <Button
                    buttonType={ButtonType.secondary}
                    onClick={(): void => navigate(`/bookings/${booking.id}/reschedule`)}
                  >
                    Reschedule
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Parent reschedule CTAs */}
          {isParentReschedule && (
            <div className='mb-4 text-center'>
              <Button
                buttonType={ButtonType.secondary}
                onClick={(): void => {
                  trackBookingAction('decline-reschedule');
                  setDeclineOpen(true);
                }}
                className='mr-2 !border-red-600 !text-red-600 hover:!bg-red-50 hover:!border-red-700 hover:!text-red-700'
              >
                Decline
              </Button>
              <Button
                buttonType={ButtonType.primary}
                onClick={acceptBooking}
                disabled={accepting}
              >
                Accept
              </Button>
            </div>
          )}

          <BookingDetailsCard booking={booking} />

          {/* Pending CTAs — shown below booking details, matching Angular layout */}
          {isPending && (
            <div className='mb-4'>
              <div className='mb-2 text-left'>
                &ldquo;Accept&rdquo; means you accept all the job details.
              </div>
              <div className='flex gap-3'>
                <Button
                  id='decline-booking'
                  buttonType={ButtonType.secondary}
                  onClick={(): void => {
                    trackBookingAction('decline-booking');
                    setDeclineOpen(true);
                  }}
                  className='!border-red-600 !text-red-600 hover:!bg-red-50 hover:!border-red-700 hover:!text-red-700'
                >
                  Decline
                </Button>
                <Button
                  id='accept-pending'
                  buttonType={ButtonType.primary}
                  onClick={acceptBooking}
                  disabled={accepting}
                >
                  Accept
                </Button>
              </div>
            </div>
          )}

          <div className='mb-4'>
            <div className='font-semibold mb-1'>{t('pages.bookingDetail.reliabilityHeading')}</div>
            <p>
              {t('pages.bookingDetail.reliabilityBody')}{' '}
              <a
                id='reliability-policy'
                target='_blank'
                rel='noreferrer'
                href='https://urbansitter.zendesk.com/hc/en-us/articles/200827526-Reliability-Policy'
                className='underline'
              >
                {t('pages.bookingDetail.readMore')}
              </a>
              .
            </p>
          </div>
        </div>

        {/* Right sidebar */}
        {showRightSidebar && (
          <div id='calendar-side-panel' className='space-y-3'>
            {showAvailabilitySidebar && (
              <>
                <h3 className='text-sm font-semibold text-neutral-800 uppercase tracking-wide'>
                  Your availability
                </h3>
                {availabilities.map((avail) => {
                  const slotState: 'open' | 'requested' | 'booked' = !avail.booking
                    ? 'open'
                    : avail.booking.status?.type === 'pending'
                      ? 'requested'
                      : 'booked';
                  const cardClass = {
                    open: 'rounded-lg p-4 text-sm bg-green-50 border border-green-200 border-l-4 border-l-green-600 shadow-sm',
                    requested: 'rounded-lg p-4 text-sm bg-honey-50 border border-honey-300 border-l-4 border-l-honey-500 shadow-sm',
                    booked: 'rounded-lg p-4 text-sm bg-sapphire-50 border border-sapphire-200 border-l-4 border-l-sapphire shadow-sm',
                  }[slotState];
                  const badgeClass = {
                    open: 'bg-green-600',
                    requested: 'bg-honey-500',
                    booked: 'bg-sapphire',
                  }[slotState];
                  const badgeLabel = {
                    open: t('pages.bookingDetail.slotAvailable'),
                    requested: t('pages.bookingDetail.requested'),
                    booked: t('pages.bookingDetail.slotBooked'),
                  }[slotState];
                  return (
                    <div key={avail.id} className={cardClass}>
                      <div className='font-semibold text-neutral-900'>
                        {getShortWeekdayDateString(avail.start)}
                      </div>
                      {avail.start && (
                        <div className='text-neutral-700 mt-0.5'>
                          {getTimeOfDayString(avail.start)}
                          {avail.end ? ` – ${getTimeOfDayString(avail.end)}` : ''}
                        </div>
                      )}
                      <div className={`mt-2 inline-block px-2 py-0.5 rounded-full text-xs font-semibold text-white ${badgeClass}`}>
                        {badgeLabel}
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {showPayoutWidget && (
              <WidgetCard
                iconName={faMoneyCheckDollar}
                iconClasses='text-gray'
                title='Setup your payout information now to have instant access to your funds'
                description='Activate your account with our partner, Branch, to easily access your funds upon payment'
                hasButton
                buttonComponent={
                  <Button
                    buttonType={ButtonType.primary}
                    onClick={(): void => {
                      window.location.href = '/app/sitter/payout';
                    }}
                  >
                    Setup payout
                  </Button>
                }
              />
            )}
          </div>
        )}
      </div>

      <CancelBookingModal
        isOpen={cancelOpen}
        onClose={(): void => setCancelOpen(false)}
        booking={booking}
        isSitter
        onCancelled={(): void => navigate('/bookings')}
      />
      <DeclineBookingModal
        isOpen={declineOpen}
        onClose={(): void => setDeclineOpen(false)}
        booking={booking}
        onDeclined={(): void => navigate('/bookings')}
      />
      <Modal
        isOpen={payoutModalOpen}
        onClose={(): void => setPayoutModalOpen(false)}
        title={t('pages.bookingDetail.payoutModalTitle')}
        className='w-full max-w-md'
        footer={
          <>
            <Button
              buttonType={ButtonType.tertiary}
              onClick={(): void => setPayoutModalOpen(false)}
            >
              {t('pages.bookingDetail.payoutModalCancel')}
            </Button>
            <Button
              id='setup-payout'
              buttonType={ButtonType.primary}
              onClick={(): void => {
                window.location.assign('/app/sitter/payout');
              }}
            >
              {t('pages.bookingDetail.payoutModalSetup')}
            </Button>
          </>
        }
      >
        <p className='text-neutral-700'>{t('pages.bookingDetail.payoutModalBody')}</p>
      </Modal>
    </div>
  );
});
